import { Haptics, ImpactStyle, NotificationType } from '@capacitor/haptics';

/** Subtle tap — for UI navigation and minor interactions (replaces vibrate 5/10/15/20ms) */
export const hapticLight = (): void => {
  Haptics.impact({ style: ImpactStyle.Light }).catch(() => {});
};

/** Heavier tap — for confirmations, sends, expands (replaces vibrate 30/50ms) */
export const hapticMedium = (): void => {
  Haptics.impact({ style: ImpactStyle.Medium }).catch(() => {});
};

/** Semantic outcome feedback — success, error, or warning */
export const hapticNotification = (type: NotificationType): void => {
  Haptics.notification({ type }).catch(() => {});
};

/**
 * Global tap-haptic. Mount once at the app root. Fires a haptic on every tap of
 * an interactive element (button, link, role=button, [data-haptic]) so the whole
 * app feels native without wiring each control. iOS owns the haptic, not each call site.
 *
 * Opt out with `data-no-haptic`. Override strength with `data-haptic="medium"`.
 * Uses pointerup (fires once per tap; no touch+mouse double-fire) in capture phase
 * so it still runs even if a handler calls stopPropagation.
 *
 * `.cursor-pointer` is included so clickable <div> cards (the common Tailwind
 * pattern, e.g. /club cards) buzz too — not just semantic buttons/links.
 */
export const installGlobalTapHaptics = (): (() => void) => {
  const SELECTOR =
    'button, a[href], [role="button"], [role="tab"], [role="menuitem"], [role="switch"], input[type="checkbox"], input[type="radio"], [data-haptic], .cursor-pointer';

  const onPointerUp = (e: PointerEvent): void => {
    // Only real taps (touch/pen), not synthetic or mouse on desktop.
    if (e.pointerType === 'mouse') return;
    const target = e.target as HTMLElement | null;
    const el = target?.closest<HTMLElement>(SELECTOR);
    if (!el) return;
    if (el.hasAttribute('data-no-haptic')) return;
    if (el.getAttribute('aria-disabled') === 'true' || (el as HTMLButtonElement).disabled) return;

    if (el.getAttribute('data-haptic') === 'medium') {
      hapticMedium();
    } else {
      hapticLight();
    }
  };

  document.addEventListener('pointerup', onPointerUp, true);
  return () => document.removeEventListener('pointerup', onPointerUp, true);
};
